import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useNotificationContext } from "@/contexts/NotificationContext";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { badgeEntrance, badgeCount, getBellShake } from "@/lib/motion";
import NotificationPanel from "./NotificationPanel";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

// ─── Types ───────────────────────────────────────────────────────────────────

interface NotificationBellProps {
  /** Additional CSS classes for the container. */
  className?: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Notification bell icon displayed in the app header for authenticated users.
 *
 * Features:
 * - Bell icon (Lucide `Bell`) with click to open notification panel popover
 * - Unread count badge:
 *   - Small red circle with white number, positioned top-right
 *   - Animated entrance with spring physics when count goes from 0 to >0
 *   - Number animates when count changes
 *   - Shows "9+" for counts above 9
 *   - Hidden when count is 0
 * - Bell shake animation when a new notification arrives:
 *   - Rotation keyframes: 0° → 15° → -15° → 10° → -10° → 0° over 0.5s
 *   - Triggered when unreadCount increases
 * - Accessible: `aria-label` with unread count
 */
export default function NotificationBell({ className }: NotificationBellProps) {
  const { t } = useTranslation();
  const { unreadCount } = useNotificationContext();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);
  const prevUnreadCountRef = useRef(unreadCount);

  // ── Trigger shake animation when unread count increases ──────────────────

  useEffect(() => {
    if (unreadCount > prevUnreadCountRef.current && unreadCount > 0) {
      // Only trigger shake if motion is allowed
      if (!prefersReducedMotion) {
        setShouldShake(true);
        const timer = setTimeout(() => setShouldShake(false), 600);
        return () => clearTimeout(timer);
      }
    }
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount, prefersReducedMotion]);

  // ── Display text for the badge ───────────────────────────────────────────

  const badgeText = unreadCount > 9 ? "9+" : String(unreadCount);

  const ariaLabel =
    unreadCount === 0
      ? t("notification.title")
      : unreadCount === 1
        ? t("notifications.unread_count_one")
        : t("notifications.unread_count_other", { count: unreadCount });

  // ── Bell shake animation (from motion.ts presets) ────────────────────────
  // Uses the standardized bellShake preset with the rotation keyframes
  // 0° → 20° → -18° → 14° → -10° → 0° over 600ms

  const bellAnimation = getBellShake(prefersReducedMotion, shouldShake);

  // ── Badge animation variants (from motion.ts presets) ────────────────────
  // Uses badgeEntrance (spring.bouncy) and badgeCount presets

  const badgeVariants = prefersReducedMotion
    ? {
        initial: { scale: 1, opacity: 1 },
        animate: { scale: 1, opacity: 1, transition: { duration: 0 } },
        exit: { scale: 0, opacity: 0, transition: { duration: 0 } },
      }
    : badgeEntrance;

  const countVariants = prefersReducedMotion
    ? {
        initial: { y: 0, opacity: 1 },
        animate: { y: 0, opacity: 1, transition: { duration: 0 } },
        exit: { y: 0, opacity: 0, transition: { duration: 0 } },
      }
    : badgeCount;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative h-9 w-9 rounded-full", className)}
          aria-label={ariaLabel}
        >
          {/* Bell icon with shake animation (standardized preset) */}
          <motion.div
            animate={bellAnimation}
            className="flex items-center justify-center"
          >
            <Bell className="h-5 w-5" />
          </motion.div>

          {/* Unread count badge */}
          <AnimatePresence mode="wait">
            {unreadCount > 0 && (
              <motion.div
                key="badge"
                variants={badgeVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className={cn(
                  "absolute -right-0.5 -top-0.5 flex items-center justify-center rounded-full bg-destructive",
                  unreadCount > 9
                    ? "h-[18px] min-w-[18px] px-1"
                    : "h-[18px] w-[18px]",
                )}
              >
                <AnimatePresence mode="wait">
                  <motion.span
                    key={badgeText}
                    variants={countVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="text-[10px] font-bold leading-none text-destructive-foreground"
                  >
                    {badgeText}
                  </motion.span>
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="w-[380px] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <NotificationPanel onClose={() => setIsOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
